import {
  type HttpErrorResponse,
  type HttpEvent,
  type HttpHandler,
  HttpHeaders,
  type HttpInterceptor,
  type HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { from, type Observable, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from '@auth/repository';
import { getSessionUser } from '@auth/repository/auth.repository';
import { CsrfTokenService } from '@core/services/csrf-token.service';
import { environment } from '@env/environment';

export const XSRF_TOKEN_NAME = 'X-XSRF-TOKEN';
export const ERROR_TOKEN_NAME = 'X-CSRF-ERROR';

@Injectable()
export class AppInterceptor implements HttpInterceptor {
  private csrfService: CsrfTokenService = inject(CsrfTokenService);
  private authService: AuthService = inject(AuthService);
  private isRefreshing = false;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let _clone: HttpRequest<any>;
    const _token: string | null = this.csrfService.getToken();

    if (request.url.startsWith('/')) {
      let headers: HttpHeaders = this._setHeaders(request);

      if (_token) {
        headers = headers.set(XSRF_TOKEN_NAME, _token);
      } else {
        // console.error('CSRF token not found: ' + request.url);
      }

      _clone = request.clone({
        url: environment.API + request.url,
        withCredentials: true,
        headers,
      });
    } else {
      _clone = request;
    }

    return next.handle(_clone).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 403 &&
          error.headers.get(ERROR_TOKEN_NAME) === 'TokenExpired' &&
          !this.isRefreshing
        ) {
          this.isRefreshing = true;

          return from(this.csrfService.refreshToken()).pipe(
            switchMap((): Observable<HttpEvent<any>> => {
              const newToken = this.csrfService.getToken();
              let headers: HttpHeaders = this._setHeaders(request);

              if (!newToken) {
                console.error(`${ERROR_TOKEN_NAME}: session expired`);
                this.authService.logout();
                return throwError(() => error);
              }

              headers = headers.set(XSRF_TOKEN_NAME, newToken);

              const retryReq = request.clone({
                url: environment.API + request.url,
                withCredentials: true,
                headers,
              });

              return next.handle(retryReq);
            }),
            // Reset the flag in any case
            switchMap((res) => {
              this.isRefreshing = false;
              return from([res]);
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private _setHeaders(request: HttpRequest<any>): HttpHeaders {
    const companyId: string =
      request.headers.get('X-ume-CompanyId') || getSessionUser().selectedCompany?.guid || '';
    let headers: HttpHeaders = new HttpHeaders();

    request.headers.keys().forEach((key) => {
      const value = request.headers.get(key);
      if (value !== null) {
        headers = headers.set(key, value);
      }
    });

    headers = headers.set('X-ume-CompanyId', companyId);

    return headers;
  }
}
